import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Logo } from "@/components/Logo";
import { VideoPlayer } from "@/components/VideoPlayer";
import { BookingModal } from "@/components/BookingModal";
import { Outcomes } from "@/sections/Outcomes";
import { Marquee } from "@/components/Marquee";
import { Framework } from "@/sections/Framework";

export default function Experience() {
  const [params] = useSearchParams();
  const name = params.get("name");
  const ref = params.get("ref");
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let id;
    const raf = (time) => {
      lenis.raf(time);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
    };
  }, []);

  return (
    <main data-testid="experience-page" className="bg-ink">
      {/* confirmation strip */}
      <div className="border-b border-[rgba(163,74,107,0.2)] bg-[#F4E8E4]">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 px-6 py-5 sm:px-10">
          <Logo compact />
          <div data-testid="experience-confirmation" className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-2 rounded-full border border-[#A34A6B]/30 bg-elevated px-4 py-1.5 text-xs text-cream">
              <Check className="h-3.5 w-3.5 text-[#A34A6B]" />
              Your place is reserved{name ? `, ${name}` : ""}
            </span>
            {ref ? (
              <span data-testid="waitlist-reference" className="rounded-full border border-[#A34A6B]/30 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#A34A6B]">
                Ref · {ref}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* welcome intro */}
      <section className="mx-auto max-w-4xl px-6 pb-8 pt-16 sm:px-10 sm:pt-24">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mb-5 text-xs font-semibold uppercase tracking-[0.3em] text-[#A34A6B]"
        >
          Welcome to the reservation
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif text-3xl leading-[1.15] text-cream sm:text-5xl"
        >
          Your place is held{name ? <>, <span className="italic text-[#A34A6B]">{name}</span></> : null}.
          <span className="block">Here's what you've just stepped towards.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-lilac"
        >
          Take a moment to explore the thinking behind the Menopause Reclaim Retreat: the outcomes it's designed
          around, the framework that shapes it, and the woman creating it.
        </motion.p>
      </section>

      {/* video message + founder story */}
      <section data-testid="experience-video-section" className="mx-auto max-w-6xl px-6 pb-24 sm:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#A34A6B]">A message from Marie</p>
            <h2 className="mb-8 max-w-xl font-serif text-2xl leading-snug text-cream sm:text-3xl">
              Two quiet minutes, <span className="italic text-[#A34A6B]">before you explore.</span>
            </h2>
            <VideoPlayer />
            <button
              type="button"
              onClick={() => setBookingOpen(true)}
              data-testid="video-discovery-call-button"
              className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#A34A6B] px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDF6F2] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#8E3D5B] hover:shadow-[0_0_36px_rgba(163,74,107,0.3)] active:scale-[0.98]"
            >
              Book a discovery call
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>

          <motion.div
            data-testid="founder-section"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="font-serif text-2xl leading-snug text-cream sm:text-3xl">Why I created this retreat</h2>
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-lilac">
              <p className="font-serif text-lg text-cream">
                Hi, I'm Marie, founder of Meno Flames &amp; Finesse and creator of the Menopause Reclaim Retreat.
              </p>
              <p>
                The Menopause Reclaim Retreat is a supportive experience for high-achieving women who are ready
                to make space for themselves and their next chapter.
              </p>
            </div>
            <blockquote className="mt-6 border-l-2 border-[#A34A6B]/40 pl-5 font-serif text-base italic text-[#A34A6B]/90">
              Because menopause doesn't come with a manual. We give women a roadmap.
            </blockquote>
            <p className="mt-4 text-sm leading-relaxed text-lilac">I'd love you to be part of the journey.</p>
            <div className="mt-6 flex items-center gap-4">
              <img
                src="/founder-portrait.png"
                alt="Marie, founder of Meno Flames & Finesse, seated outdoors in warm light"
                className="h-20 w-20 rounded-2xl border border-[rgba(163,74,107,0.25)] object-cover object-[center_18%]"
              />
              <div>
                <p className="font-serif text-xl italic text-cream">Marie</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-mutedlilac">Founder, Meno Flames &amp; Finesse</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Framework />
      <Marquee />
      <Outcomes onBookClick={() => setBookingOpen(true)} />

      <footer className="border-t border-[rgba(163,74,107,0.18)]">
        <p className="mx-auto max-w-5xl px-6 py-8 text-xs text-mutedlilac sm:px-10">
          Meno Flames &amp; Finesse · Menopause Reclaim Retreat, United Kingdom · Your details are stored securely and never sold.
        </p>
      </footer>

      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} defaultName={name || ""} />
    </main>
  );
}
